import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, Drawer, Empty, InputNumber, Skeleton, Space, Table, Tooltip, message } from "antd";
import { CaretDownOutlined, CaretRightOutlined, LeftOutlined, RightOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  DaySheetScope,
  DaySheetTask,
  EQUIPMENT_HEAD_KEYS,
  EquipmentHeadHours,
  EquipmentHeadKey,
  EquipmentLine,
  EquipmentSaveLine,
  EquipmentSheet,
  normaliseEquipmentSheet,
  siteDailyApis,
  VARIABLE_HEAD_KEYS,
  FixedHeadKey,
  VariableHeadKey,
} from "../../../../api/SiteDaily";
import { useFontScale } from "../../../../theme/fontScale";
import { fmtQty } from "../executionNorm";
import DaySheetTree from "./DaySheetTree";
import PendingByDateList from "./PendingByDateList";
import CivilTaskFilterBar from "./CivilTaskFilterBar";
import {
  SheetSectionHandle,
  SheetSectionMeta,
  fromInput,
  inputValue,
  isPresent,
  saveSequentially,
  sheetMeta,
  useDaySheet,
  filterCivilSheet,
  perUnitOf,
  perUnitLabel,
  fmtPerUnit,
} from "./daySheet";

// Mechanical: hours worked per machine deployed on each produced task today,
// with the cost heads beneath — three FIXED heads priced from the equipment
// rate card × hours worked (read-only), five CONSUMED heads captured as
// hours × actual ₹/hr — the rate card's own unit, so the report's target and
// actual rates compare. Money is shown here by the
// user's decision; the fuel / meter / breakdown fields stay on the mobile
// form and pass through a web save untouched.

const MAX_HOURS = 24;
const VARIABLE_HEAD_LABEL: Record<VariableHeadKey, string> = {
  repair: "Repair",
  misc: "Misc",
  electricity: "Electricity",
  fuel: "Fuel",
  lubricants: "Lubricants",
};
const FIXED_HEAD_LABEL: Record<FixedHeadKey, string> = {
  depreciation: "Depreciation",
  interest: "Interest",
  insurance: "Insurance",
};

interface HeadDraft {
  qty: string;
  uomId: number;
  rate: string;
}
type HeadDrafts = Record<VariableHeadKey, HeadDraft>;

const HEAD_LABEL: Record<EquipmentHeadKey, string> = {
  depreciation: "Depreciation",
  interest: "Interest",
  repair: "Repair",
  misc: "Misc",
  insurance: "Insurance",
  electricity: "Electricity",
  fuel: "Fuel",
  lubricants: "Lubricants",
};

interface LineDraft {
  // Today's own hours (the day-wise entry)...
  hoursWorked: string;
  // ...plus hours entered against earlier civil dates, keyed by work date.
  // Saved as ONE log (hoursWorked + Σ pendingDays) with this split beside it:
  // each part is booked on its civil date exactly (as on Material-Stores).
  pendingDays: Record<string, string>;
  fuelLitres: string;
  readingStart: string;
  readingEnd: string;
  isBreakdown: boolean;
  idleReason: string;
  headHours: EquipmentHeadHours;
  heads: HeadDrafts;
}

interface TaskLines {
  loading: boolean;
  error: string;
  sheet: EquipmentSheet | null;
  initial: Record<number, LineDraft>;
  draft: Record<number, LineDraft>;
  saving: boolean;
}

// Consumed heads start from what is stored, else from the head's master
// defaults (unit always; the diesel / electricity price where one exists).
const headDraftsOf = (line: EquipmentLine): HeadDrafts => {
  const drafts = {} as HeadDrafts;
  VARIABLE_HEAD_KEYS.forEach((key) => {
    const h = line.variableHeads.find((x) => x.head === key);
    drafts[key] = {
      qty: h?.qty ?? "",
      uomId: h?.defaultUomId || h?.uomId || 0,
      // No prefill (user rule): the rate is what the clerk types; only a
      // stored rate reads back.
      rate: h?.rateSource === "ENTERED" ? h.rate : "",
    };
  });
  return drafts;
};

/** Today's saved hours split back into what it entered per civil date and the rest. */
const splitSavedHours = (line: EquipmentLine): { hoursWorked: string; pendingDays: Record<string, string> } => {
  const pendingDays: Record<string, string> = {};
  let onDays = 0;
  line.civilDays.forEach((day) => {
    const v = Number(day.bookedTodayQty);
    if (v > 0) {
      pendingDays[day.workDate] = day.bookedTodayQty;
      onDays += v;
    }
  });
  if (line.hoursWorked === "" || onDays <= 0) return { hoursWorked: line.hoursWorked, pendingDays: {} };
  const rest = Math.round((Number(line.hoursWorked) - onDays) * 10000) / 10000;
  return { hoursWorked: rest > 0.00005 ? String(rest) : "", pendingDays };
};

/** The machine's whole log for the day: today's hours + Σ civil-date parts. */
const totalHours = (draft: LineDraft): string => {
  const days = Object.values(draft.pendingDays).reduce((sum, v) => sum + (isPresent(v) ? Number(v) : 0), 0);
  if (days <= 0) return draft.hoursWorked;
  return String(Math.round(((isPresent(draft.hoursWorked) ? Number(draft.hoursWorked) : 0) + days) * 10000) / 10000);
};

const sameDays = (a: Record<string, string>, b: Record<string, string>) => {
  const filled = (days: Record<string, string>) =>
    Object.entries(days)
      .filter(([, v]) => v !== "")
      .sort(([x], [y]) => x.localeCompare(y))
      .join("|");
  return filled(a) === filled(b);
};

const draftOf = (line: EquipmentLine): LineDraft => ({
  ...splitSavedHours(line),
  fuelLitres: line.fuelLitres,
  readingStart: line.readingStart,
  readingEnd: line.readingEnd,
  isBreakdown: line.isBreakdown,
  idleReason: line.idleReason,
  headHours: { ...line.headHours },
  heads: headDraftsOf(line),
});

const sameHead = (a: HeadDraft, b: HeadDraft) => a.qty === b.qty && a.uomId === b.uomId && a.rate === b.rate;

const sameDraft = (a: LineDraft | undefined, b: LineDraft | undefined) =>
  !!a &&
  !!b &&
  a.hoursWorked === b.hoursWorked &&
  sameDays(a.pendingDays, b.pendingDays) &&
  a.fuelLitres === b.fuelLitres &&
  a.readingStart === b.readingStart &&
  a.readingEnd === b.readingEnd &&
  a.isBreakdown === b.isBreakdown &&
  a.idleReason === b.idleReason &&
  EQUIPMENT_HEAD_KEYS.every((key) => a.headHours[key] === b.headHours[key]) &&
  VARIABLE_HEAD_KEYS.every((key) => sameHead(a.heads[key], b.heads[key]));

const isDirty = (entry: TaskLines | undefined) =>
  !!entry?.sheet && entry.sheet.lines.some((line) => !sameDraft(entry.draft[line.assetId], entry.initial[line.assetId]));

// Blocking problems on one machine's line. The meter note (end before start)
// is deliberately NOT here — it is shown, but does not stop the save.
const lineErrors = (draft: LineDraft | undefined): string[] => {
  if (!draft) return [];
  const errors: string[] = [];
  if (isPresent(draft.hoursWorked) && (Number(draft.hoursWorked) > MAX_HOURS || Number(draft.hoursWorked) < 0)) {
    errors.push(`Hours worked must be 0–${MAX_HOURS}`);
  }
  // 24 h is per day of WORK: each civil date's hours, not the log's total.
  Object.entries(draft.pendingDays).forEach(([workDate, value]) => {
    if (isPresent(value) && (Number(value) > MAX_HOURS || Number(value) < 0)) {
      errors.push(`Hours for ${dayjs(workDate).format("DD-MMM")} must be 0–${MAX_HOURS}`);
    }
  });
  EQUIPMENT_HEAD_KEYS.forEach((key) => {
    const value = draft.headHours[key];
    if (isPresent(value) && (Number(value) > MAX_HOURS || Number(value) < 0)) {
      errors.push(`${HEAD_LABEL[key]} hours must be 0–${MAX_HOURS}`);
    }
  });
  if (draft.isBreakdown && draft.idleReason.trim() === "") {
    errors.push("A breakdown needs a reason");
  }
  VARIABLE_HEAD_KEYS.forEach((key) => {
    const h = draft.heads[key];
    if (isPresent(h.qty) && Number(h.qty) < 0) errors.push(`${VARIABLE_HEAD_LABEL[key]} quantity cannot be negative`);
    if (isPresent(h.rate) && Number(h.rate) < 0) errors.push(`${VARIABLE_HEAD_LABEL[key]} rate cannot be negative`);
  });
  return errors;
};

// ₹ helpers for the panel — qty × rate, hours × ₹/hr, both '' unless both
// sides are present. Money is formatted with the same 2-dp figure formatter.
const times = (a: string, b: string): number | null =>
  isPresent(a) && isPresent(b) && Number.isFinite(Number(a)) && Number.isFinite(Number(b))
    ? Math.round(Number(a) * Number(b) * 100) / 100
    : null;
const fixedAmount = (hours: string, ratePerHour: string): number | null => times(hours, ratePerHour);
const consumedAmount = (h: HeadDraft): number | null => times(h.qty, h.rate);
type CivilDay = EquipmentLine["civilDays"][number];
/**
 * Hours the FIXED heads (depreciation, interest, insurance) are charged on:
 * the ACTUAL hours, priced at the rate card's (norm) ₹/hr (user, 2026-10-09).
 * On a civil date, the hours in that date's Actual hrs box — earlier logs'
 * booking plus this log's share; without a date, this log's whole hours.
 */
const fixedHoursOf = (_line: EquipmentLine, draft: LineDraft, day?: CivilDay): string => {
  if (!day) return totalHours(draft);
  const earlier = Math.max(
    0,
    (isPresent(day.bookedQty) ? Number(day.bookedQty) : 0) - (isPresent(day.bookedTodayQty) ? Number(day.bookedTodayQty) : 0)
  );
  const share = draft.pendingDays[day.workDate] ?? "";
  const hours = earlier + (isPresent(share) ? Number(share) : 0);
  return hours > 0 ? String(Math.round(hours * 10000) / 10000) : "";
};
const machineTotal = (
  line: EquipmentLine,
  draft: LineDraft,
  day?: CivilDay
): { fixed: number; consumed: number; total: number } => {
  const fixedHours = fixedHoursOf(line, draft, day);
  const fixed = line.fixedHeads.reduce((sum, h) => sum + (fixedAmount(fixedHours, h.ratePerHour) ?? 0), 0);
  const consumed = VARIABLE_HEAD_KEYS.reduce((sum, key) => sum + (consumedAmount(draft.heads[key]) ?? 0), 0);
  return { fixed, consumed, total: fixed + consumed };
};

const isInvalid = (entry: TaskLines | undefined) =>
  !!entry?.sheet && entry.sheet.lines.some((line) => lineErrors(entry.draft[line.assetId]).length > 0);

interface Props {
  projectId: number;
  date: string; // 'YYYY-MM-DD'
  scope: DaySheetScope;
  phaseId?: number;
  activityId?: number;
  reloadToken: number;
  onSheetChange: (meta: SheetSectionMeta) => void;
  onDirtyChange: (count: number) => void;
}

const MechanicalSection = forwardRef<SheetSectionHandle, Props>(
  ({ projectId, date, scope, phaseId, activityId, reloadToken, onSheetChange, onDirtyChange }, ref) => {
    // Machine hours are booked against civil-completed TASKS, not a day (see
    // Material-Stores): every task with civil done, stamped with today.
    const { sheet, loading, error, reload } = useDaySheet(
      "equipment",
      projectId,
      { date, scope, phaseId, activityId, civilDone: true },
      reloadToken
    );
    const locked = !!sheet?.dayLocked;
    // Civil-done basis: default to tasks still owed machine hours, or every civil-done
    // task for extra / corrective entries; plus a search.
    const [pendingOnly, setPendingOnly] = useState(true);
    const [taskSearch, setTaskSearch] = useState("");
    // Owed machine hours, or a civil date today's log booked (listed so it can
    // be corrected) — both keep the task in "Pending machine hours".
    const pendingOf = useCallback(
      (task: DaySheetTask) => task.pendingEquipmentCount || (task.pendingEquipmentDates.length > 0 ? 1 : 0),
      []
    );
    const shownSheet = useMemo(
      () => filterCivilSheet(sheet, { pendingOnly, search: taskSearch, pendingOf }),
      [sheet, pendingOnly, taskSearch, pendingOf]
    );
    const civilCounts = useMemo(() => {
      const all = (sheet?.phases ?? []).flatMap((p) => p.activities).flatMap((a) => a.tasks);
      return { all: all.length, pending: all.filter((t) => pendingOf(t) > 0).length };
    }, [sheet, pendingOf]);

    // A task opens in a DRAWER (as on Material-Stores), not inline under its
    // row. The order the drawer walks is frozen when it opens, so a save that
    // drops a task from the pending list never makes "next" skip one.
    const [openTaskId, setOpenTaskId] = useState<number | null>(null);
    // A stop is a task, or a task ON a civil date when opened from the
    // date-wise Pending list (the drawer then shows that date only).
    const [navOrder, setNavOrder] = useState<Array<{ taskId: number; date: string | null }>>([]);
    const [focusDate, setFocusDate] = useState<string | null>(null);
    const [linesByTask, setLinesByTask] = useState<Record<number, TaskLines>>({});
    const linesRef = useRef(linesByTask);
    linesRef.current = linesByTask;
    // A response for a day the user has since left must never land in the
    // new day's cache (a replace-set save would then copy the old day over).
    const dateRef = useRef(date);
    dateRef.current = date;

    useEffect(() => {
      setLinesByTask({});
      setOpenTaskId(null);
    }, [projectId, date]);

    useEffect(() => {
      onSheetChange(sheetMeta(sheet, loading));
    }, [sheet, loading, onSheetChange]);

    const dirtyIds = useMemo(
      () =>
        Object.entries(linesByTask)
          .filter(([, entry]) => isDirty(entry))
          .map(([taskId]) => Number(taskId)),
      [linesByTask]
    );
    useEffect(() => {
      onDirtyChange(dirtyIds.length);
    }, [dirtyIds, onDirtyChange]);

    const patchTask = useCallback((taskId: number, patch: Partial<TaskLines>) => {
      setLinesByTask((current) => ({
        ...current,
        [taskId]: { ...(current[taskId] || emptyTaskLines()), ...patch },
      }));
    }, []);

    const fetchLines = useCallback(
      async (taskId: number) => {
        const forDate = date;
        patchTask(taskId, { loading: true, error: "" });
        const res = await siteDailyApis.getEquipment(taskId, forDate);
        if (dateRef.current !== forDate) return;
        if (res?.status !== true) {
          patchTask(taskId, { loading: false, error: res?.message || "Could not load equipment lines" });
          return;
        }
        const next = normaliseEquipmentSheet(res.data);
        if (next.logDate && next.logDate !== forDate) return;
        const initial: Record<number, LineDraft> = {};
        next.lines.forEach((line) => {
          initial[line.assetId] = draftOf(line);
        });
        setLinesByTask((current) => ({
          ...current,
          [taskId]: { loading: false, error: "", sheet: next, initial, draft: { ...initial }, saving: false },
        }));
      },
      [date, patchTask]
    );

    // Lazy: a task's lines load the first time it is opened, and stay (with
    // their draft) while the drawer moves on or closes.
    const showTask = (taskId: number) => {
      setOpenTaskId(taskId);
      const entry = linesRef.current[taskId];
      if (!entry || (entry.error && !entry.loading)) fetchLines(taskId);
    };
    const shownOrder = useMemo(
      () => (shownSheet?.phases || []).flatMap((p) => p.activities.flatMap((a) => a.tasks.map((t) => t.taskId))),
      [shownSheet]
    );
    const tasksById = useMemo(() => {
      const map = new Map<number, DaySheetTask>();
      (sheet?.phases || []).forEach((p) => p.activities.forEach((a) => a.tasks.forEach((t) => map.set(t.taskId, t))));
      return map;
    }, [sheet]);
    const showStop = (stop: { taskId: number; date: string | null }) => {
      setFocusDate(stop.date);
      showTask(stop.taskId);
    };
    const openFromList = (task: DaySheetTask) => {
      setNavOrder(shownOrder.map((taskId) => ({ taskId, date: null })));
      showStop({ taskId: task.taskId, date: null });
    };
    // The date-wise list's order: civil date by civil date, oldest first.
    const dateOrder = useMemo(() => {
      const stops: Array<{ taskId: number; date: string | null }> = [];
      (shownSheet?.phases || []).forEach((p) =>
        p.activities.forEach((a) =>
          a.tasks.forEach((t) => t.pendingEquipmentDates.forEach((d) => stops.push({ taskId: t.taskId, date: d.workDate })))
        )
      );
      return stops.sort((x, y) => (x.date as string).localeCompare(y.date as string));
    }, [shownSheet]);
    const openFromDate = (task: DaySheetTask, workDate: string) => {
      setNavOrder(dateOrder);
      showStop({ taskId: task.taskId, date: workDate });
    };

    const setLineDraft = (taskId: number, assetId: number, patch: Partial<LineDraft>) => {
      setLinesByTask((current) => {
        const entry = current[taskId];
        if (!entry) return current;
        return {
          ...current,
          [taskId]: { ...entry, draft: { ...entry.draft, [assetId]: { ...entry.draft[assetId], ...patch } } },
        };
      });
    };

    const resetTask = (taskId: number) => {
      const entry = linesRef.current[taskId];
      if (!entry) return;
      patchTask(taskId, { draft: { ...entry.initial } });
    };

    const saveTask = useCallback(
      async (taskId: number, quiet = false): Promise<boolean> => {
        const entry = linesRef.current[taskId];
        if (!entry?.sheet || locked || isInvalid(entry)) return false;
        // Every machine goes up as it stands — blank fields stay '' — and the
        // server drops the lines with nothing recorded.
        const lines: EquipmentSaveLine[] = entry.sheet.lines.map((line) => {
          const draft = entry.draft[line.assetId] || draftOf(line);
          return {
            assetId: line.assetId,
            // ONE log per machine: today's hours plus the civil-date parts,
            // which go up beside it and are booked on their dates exactly.
            hoursWorked: totalHours(draft),
            civilDates: Object.entries(draft.pendingDays)
              .filter(([, qty]) => qty !== "" && Number(qty) > 0)
              .map(([civilDate, qty]) => ({ civilDate, qty })),
            fuelLitres: draft.fuelLitres,
            readingStart: draft.readingStart,
            readingEnd: draft.readingEnd,
            isBreakdown: draft.isBreakdown,
            // Sent as it stands: a reason logged without the breakdown flag
            // (the site app's "idle" case) must survive a save of any other
            // machine on this task-day — the save is a replace-set.
            idleReason: draft.idleReason.trim(),
            headHours: { ...draft.headHours },
            heads: VARIABLE_HEAD_KEYS.map((key) => ({
              head: key,
              qty: draft.heads[key].qty,
              uomId: draft.heads[key].uomId,
              rate: draft.heads[key].rate,
            })),
          };
        });
        patchTask(taskId, { saving: true });
        try {
          const res = await siteDailyApis.saveEquipment({ taskId, logDate: date, lines, byCivilDate: true });
          if (res?.status !== true) {
            message.error(res?.message || "Could not save the equipment log");
            patchTask(taskId, { saving: false });
            return false;
          }
          if (!quiet) message.success(res.data?.message || "Equipment log saved");
          if (res.data?.warning) message.warning(res.data.warning, 6);
          await fetchLines(taskId);
          reload();
          return true;
        } catch (err: any) {
          message.error(err?.message || "Could not save the equipment log");
          patchTask(taskId, { saving: false });
          return false;
        }
      },
      [date, locked, patchTask, fetchLines, reload]
    );

    const taskLabel = useCallback(
      (taskId: number) => {
        for (const phase of sheet?.phases || []) {
          for (const activity of phase.activities) {
            const task = activity.tasks.find((t) => t.taskId === taskId);
            if (task) return task.taskSequenceId;
          }
        }
        return `Task ${taskId}`;
      },
      [sheet]
    );

    useImperativeHandle(
      ref,
      () => ({
        discardAll: () => {
          setLinesByTask({});
          setOpenTaskId(null);
        },
        saveAll: async () => {
          // Invalid tasks are left out and named, rather than failing the run.
          const invalid = dirtyIds.filter((id) => isInvalid(linesRef.current[id]));
          const ids = dirtyIds
            .filter((id) => !invalid.includes(id))
            .map((id) => ({ id, label: taskLabel(id) }));
          if (invalid.length) {
            message.warning(`Fix the flagged rows on ${invalid.map(taskLabel).join(", ")} first`, 6);
          }
          if (!ids.length) return;
          await saveSequentially(ids, (id) => saveTask(id, true));
        },
      }),
      [dirtyIds, saveTask, taskLabel]
    );

    // ---- the task drawer ----------------------------------------------------
    const openTask = openTaskId !== null ? tasksById.get(openTaskId) : undefined;
    const openEntry = openTaskId !== null ? linesByTask[openTaskId] : undefined;
    const navIndex =
      openTaskId !== null ? navOrder.findIndex((stop) => stop.taskId === openTaskId && stop.date === focusDate) : -1;
    const prevStop = navIndex > 0 ? navOrder[navIndex - 1] : null;
    const nextStop = navIndex >= 0 && navIndex < navOrder.length - 1 ? navOrder[navIndex + 1] : null;
    const openDirty = isDirty(openEntry);
    const openInvalid = isInvalid(openEntry);

    const saveAndNext = async () => {
      if (openTaskId === null) return;
      // The next task is fixed BEFORE the save — the save may reorder the list.
      const target = nextStop;
      const ok = await saveTask(openTaskId);
      if (ok && target !== null) showStop(target);
    };

    return (
      <>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
        {locked && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Day locked — the DPR for ${dayjs(date).format("DD-MMM-YYYY")} has been submitted`}
            description="Equipment logs for this date are read-only."
          />
        )}
        <CivilTaskFilterBar
          pendingOnly={pendingOnly}
          onPendingOnlyChange={setPendingOnly}
          search={taskSearch}
          onSearchChange={setTaskSearch}
          pendingCount={civilCounts.pending}
          allCount={civilCounts.all}
          what="machine hours"
        />
        {pendingOnly ? (
          // Pending machine hours, DATE-WISE: civil date → activity → task.
          <PendingByDateList
            sheet={shownSheet}
            loading={loading}
            activeTaskId={openTaskId}
            activeDate={focusDate}
            onOpen={openFromDate}
            datesOf={(task) => task.pendingEquipmentDates}
            lineNoun={["machine", "machines"]}
            owedTitle="Machines owed"
            taskState={(task) => {
              const entry = linesByTask[task.taskId];
              return entry ? { dirty: isDirty(entry), saving: entry.saving } : undefined;
            }}
            emptyDescription="Nothing owed — every civil date has its machine hours booked"
          />
        ) : (
          <DaySheetTree
            sheet={shownSheet}
            pendingCountOf={pendingOf}
            pendingLabel="hours"
            loading={loading}
            expandedTaskIds={[]}
            onExpandedTaskIdsChange={() => undefined}
            renderTaskDetail={() => null}
            onTaskOpen={openFromList}
            activeTaskId={openTaskId}
            taskState={(task) => {
              const entry = linesByTask[task.taskId];
              if (!entry) return undefined;
              // The task's machinery cost for the day (every machine's fixed +
              // consumed), for the task-level ₹ and ₹ / unit on the tree row.
              const amount = entry.sheet
                ? entry.sheet.lines.reduce(
                    (sum, line) => sum + machineTotal(line, entry.draft[line.assetId] || draftOf(line)).total,
                    0
                  )
                : null;
              return { dirty: isDirty(entry), invalid: isInvalid(entry), saving: entry.saving, amount };
            }}
            showTrackedTag={!!sheet?.monitoringConfigured && sheet.scope === "ALL"}
            emptyDescription={
              <span>
                No tasks produced on this date in Civil
                <br />
                <span style={{ color: "#8094ae", fontSize: 12 }}>
                  Enter the day's execution first — this sheet lists the tasks that produced output
                  (with equipment norms; and tracked, when tracking is on).
                </span>
              </span>
            }
          />
        )}
        <Drawer
          open={openTaskId !== null}
          onClose={() => setOpenTaskId(null)}
          width="min(1180px, 94vw)"
          className="pms-mat-drawer"
          title={
            openTask ? (
              <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
                <span>
                  <span className="pms-exec-task-seq">{openTask.taskSequenceId}</span>
                  <span className="pms-exec-task-name">{openTask.taskName}</span>
                </span>
                <span className="pms-exec-hint" style={{ fontWeight: 400 }}>
                  Machinery log · {dayjs(date).format("DD-MMM-YYYY")}
                  {openTask.workUomName && openTask.producedTillDate !== ""
                    ? ` · civil done ${fmtQty(openTask.producedTillDate)} ${openTask.workUomName}`
                    : ""}
                </span>
              </span>
            ) : (
              "Machinery log"
            )
          }
          extra={
            navOrder.length > 1 && navIndex >= 0 ? (
              <Space size={4}>
                <Button size="small" icon={<LeftOutlined />} disabled={prevStop === null} onClick={() => prevStop && showStop(prevStop)} aria-label="Previous task" />
                <span className="pms-exec-hint exec-num">
                  {navIndex + 1} / {navOrder.length}
                </span>
                <Button size="small" icon={<RightOutlined />} disabled={nextStop === null} onClick={() => nextStop && showStop(nextStop)} aria-label="Next task" />
              </Space>
            ) : null
          }
          footer={
            openTaskId === null ? null : (
              <div className="pms-mat-drawer-foot">
                <span className="pms-exec-hint">
                  {locked
                    ? "Day locked — read-only."
                    : openDirty
                      ? "Unsaved changes stay with this task if you move on — save them here or with Save all."
                      : "Blank = not recorded. A machine with nothing recorded is left out of the log."}
                </span>
                {!locked && (
                  <Space>
                    <Button onClick={() => resetTask(openTaskId)} disabled={!openDirty || openEntry?.saving}>
                      Reset
                    </Button>
                    <Tooltip title={openInvalid ? "Fix the flagged rows to save" : ""}>
                      <Button
                        icon={<SaveOutlined />}
                        onClick={() => saveTask(openTaskId)}
                        disabled={!openDirty || openInvalid}
                        loading={openEntry?.saving}
                      >
                        Save
                      </Button>
                    </Tooltip>
                    {nextStop !== null && (
                      <Button
                        type="primary"
                        onClick={openDirty ? saveAndNext : () => showStop(nextStop)}
                        disabled={openDirty && openInvalid}
                        loading={openEntry?.saving}
                      >
                        {openDirty ? "Save & next" : "Next task"} <RightOutlined />
                      </Button>
                    )}
                  </Space>
                )}
              </div>
            )
          }
        >
          {openTask && (
            <EquipmentLinesEditor
              key={`${openTask.taskId}-${focusDate ?? ""}`}
              task={openTask}
              focusDate={focusDate}
              entry={openEntry}
              locked={locked}
              inDrawer
              onChange={(assetId, patch) => setLineDraft(openTask.taskId, assetId, patch)}
              onReset={() => resetTask(openTask.taskId)}
              onSave={() => saveTask(openTask.taskId)}
              onRetry={() => fetchLines(openTask.taskId)}
            />
          )}
        </Drawer>
      </>
    );
  }
);

const emptyTaskLines = (): TaskLines => ({
  loading: false,
  error: "",
  sheet: null,
  initial: {},
  draft: {},
  saving: false,
});

// ------------------------------------------------------------- line editor
const EquipmentLinesEditor: React.FC<{
  task: DaySheetTask;
  entry?: TaskLines;
  locked: boolean;
  onChange: (assetId: number, patch: Partial<LineDraft>) => void;
  onReset: () => void;
  onSave: () => void;
  // In the task drawer the Reset / Save actions live in the drawer footer.
  inDrawer?: boolean;
  onRetry: () => void;
  // Opened from a civil date in the Pending list: that date only.
  focusDate?: string | null;
}> = ({ task, entry, locked, onChange, onReset, onSave, onRetry, inDrawer, focusDate }) => {
  const { scale } = useFontScale();
  const w = (base: number) => Math.round(base * scale);
  // Which machines have their cost-head split open — local to this editor.
  const [openHeads, setOpenHeads] = useState<React.Key[]>([]);
  // Civil dates opened / closed by hand; unset = open, or only the focus date
  // open when the drawer came from a date in the Pending list.
  const [dayOpen, setDayOpen] = useState<Record<string, boolean>>({});

  if (!entry || (entry.loading && !entry.sheet)) {
    return <Skeleton active title={false} paragraph={{ rows: 3 }} />;
  }
  if (entry.error && !entry.sheet) {
    return (
      <Alert
        type="error"
        showIcon
        message={entry.error}
        action={
          <Button size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }
  const sheet = entry.sheet as EquipmentSheet;
  if (!sheet.lines.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No equipment norms on this task" />;
  }

  const dirty = isDirty(entry);
  const invalid = isInvalid(entry);
  const disabled = locked || entry.saving;
  const draftFor = (line: EquipmentLine) => entry.draft[line.assetId] || draftOf(line);

  // ONE view (user, 2026-10-09): hours by CIVIL DATE — each date's machines
  // with Target hrs, the Actual hrs box and the machine's cost heads. Fuel,
  // meter readings and breakdown stay on the mobile form and the wire.
  // ₹ per unit of today's output (Cum on excavation tasks) for every cost figure.
  const unit = perUnitLabel(sheet.workUomName);
  const pu = (amount: number | null | undefined): number | null => perUnitOf(amount ?? null, sheet.producedQty);

  // Task level: every machine's day total, and that per unit of output.
  const taskTotal = sheet.lines.reduce((sum, line) => sum + machineTotal(line, draftFor(line)).total, 0);
  const taskPerUnit = taskTotal > 0 ? pu(taskTotal) : null;

  // The cost-head panel toggle of a row: the machine's ₹ for today's log and
  // how many consumed heads are filled. Heads belong to today's LOG, so a
  // machine listed under several civil dates opens the same panel each time.
  const headsToggle = (line: EquipmentLine, rowKey: React.Key, day?: CivilDay) => {
    const draft = draftFor(line);
    const consumedSet = VARIABLE_HEAD_KEYS.filter((key) => isPresent(draft.heads[key].qty)).length;
    const { total } = machineTotal(line, draft, day);
    const earlier = (day?.earlierHeads ?? []).reduce((sum, h) => sum + (isPresent(h.amount) ? Number(h.amount) : 0), 0);
    const open = openHeads.includes(rowKey);
    return (
      <Button
        type="link"
        size="small"
        style={{ padding: 0 }}
        onClick={() => setOpenHeads((current) => (open ? current.filter((key) => key !== rowKey) : [...current, rowKey]))}
      >
        {open ? <CaretDownOutlined /> : <CaretRightOutlined />}{" "}
        {/* One figure: the date's whole cost for the machine — fixed, today's
            operational and earlier logs' operational share. */}
        {total + earlier > 0 ? (
          <span className="exec-num">₹ {fmtQty(String(Math.round((total + earlier) * 100) / 100))}</span>
        ) : (
          "Cost heads"
        )}
        {consumedSet ? <span className="pms-exec-hint" style={{ marginLeft: 6 }}>{consumedSet} of 5 consumed</span> : null}
      </Button>
    );
  };

  const renderHeads = (line: EquipmentLine, day?: CivilDay) => {
    const draft = draftFor(line);
    const totals = machineTotal(line, draft, day);
    const fixedHours = fixedHoursOf(line, draft, day);
    const hoursKnown = isPresent(fixedHours);
    // Running cost earlier logs booked on this civil date (their share).
    const earlierConsumed =
      Math.round((day?.earlierHeads ?? []).reduce((sum, x) => sum + (isPresent(x.amount) ? Number(x.amount) : 0), 0) * 100) / 100;
    // ₹ per unit of THAT civil date's output when opened under a date.
    const puDay = (amount: number | null | undefined): number | null =>
      day ? perUnitOf(amount ?? null, day.producedQty) : pu(amount);
    const noteDay = (amount: number) => {
      const v = amount > 0 ? puDay(amount) : null;
      return v === null ? null : (
        <span className="pms-exec-per-unit">
          ₹ {fmtPerUnit(v)} / {unit}
        </span>
      );
    };
    const noRate = line.rateStatus === "NO_RATE";
    const setHead = (key: VariableHeadKey, patch: Partial<HeadDraft>) =>
      onChange(line.assetId, { heads: { ...draft.heads, [key]: { ...draft.heads[key], ...patch } } });

    const fixedRows = line.fixedHeads.map((h) => ({
      key: h.head,
      label: FIXED_HEAD_LABEL[h.head],
      hours: fixedHours,
      ratePerHour: h.ratePerHour,
      amount: fixedAmount(fixedHours, h.ratePerHour),
    }));
    const consumedRows = VARIABLE_HEAD_KEYS.map((key) => {
      const h = draft.heads[key];
      const meta = line.variableHeads.find((x) => x.head === key);
      return {
        key,
        label: VARIABLE_HEAD_LABEL[key],
        draft: h,
        amount: consumedAmount(h),
        uomName: meta?.defaultUomName || meta?.uomName || "",
        defaultUomName: meta?.defaultUomName || "",
        normRate: meta?.normRatePerHour ?? "",
        // Earlier logs' share of this head on THIS civil date — shown inside the
        // boxes (as on Material-Stores); today's entry is added on top of it.
        earlier: (() => {
          const rows = (day?.earlierHeads ?? []).filter((x) => x.head === key);
          if (!rows.length) return null;
          const qty = rows.reduce((sum, x) => sum + (isPresent(x.qty) ? Number(x.qty) : 0), 0);
          const amount = rows.reduce((sum, x) => sum + (isPresent(x.amount) ? Number(x.amount) : 0), 0);
          return {
            qty: Math.round(qty * 10000) / 10000,
            amount: Math.round(amount * 100) / 100,
            rate: qty > 0 ? amount / qty : null,
            from: rows.map((x) => `${dayjs(x.logDate).format("DD-MMM")} log (${fmtQty(x.hoursHere)} of ${fmtQty(x.logHours)} h)`).join(", "),
          };
        })(),
      };
    });
    // The hours the TARGET of a consumed head is read on: the hours entered for
    // this civil date (earlier logs + this log's share), else this log's own
    // hours. Target ₹ = those hours x the rate card's ₹/hr for the head.
    const targetHours = (() => {
      if (day) {
        const share = draft.pendingDays[day.workDate] ?? "";
        const earlier = Math.max(0, (isPresent(day.bookedQty) ? Number(day.bookedQty) : 0) - (isPresent(day.bookedTodayQty) ? Number(day.bookedTodayQty) : 0));
        const total = earlier + (isPresent(share) ? Number(share) : 0);
        return total > 0 ? total : null;
      }
      return isPresent(draft.hoursWorked) ? Number(draft.hoursWorked) : null;
    })();

    return (
      <div className="pms-exec-heads">
        <div className="pms-exec-heads-block">
          <div className="pms-exec-lines-head" style={{ marginBottom: 6 }}>
            <span>
              <span className="pms-eyebrow">Owning cost (Fixed heads)</span>
              <div className="pms-exec-hint">
                Actual hours{day ? ` on ${dayjs(day.workDate).format("DD-MMM")}` : ""} × the rate card's norm ₹/hr — derived, not entered
                {noRate ? " · this machine has no rate card on this date" : ""}
              </div>
            </span>
            <span className="pms-exec-heads-figure">
              <span className="exec-num pms-exec-heads-subtotal">₹ {fmtQty(totals.fixed)}</span>
              {noteDay(totals.fixed)}
            </span>
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            className="pms-exec-heads-table pms-exec-heads-table--fixed"
            dataSource={fixedRows}
            columns={[
              { title: "Head", dataIndex: "label", key: "label", width: 160 },
              {
                title: "Hours",
                key: "hours",
                width: 110,
                align: "right" as const,
                render: (_: any, row: any) => (
                  <span className="exec-num" style={{ color: hoursKnown ? "#364a63" : "#8094ae" }}>
                    {hoursKnown ? fmtQty(row.hours) : "—"}
                  </span>
                ),
              },
              {
                title: "Rate ₹/hr",
                key: "rate",
                width: 130,
                align: "right" as const,
                render: (_: any, row: any) => (
                  <span className="exec-num" style={{ color: row.ratePerHour === "" ? "#8094ae" : "#364a63" }}>
                    {row.ratePerHour === "" ? "—" : fmtQty(row.ratePerHour)}
                  </span>
                ),
              },
              {
                title: "Amount ₹",
                key: "amount",
                width: 140,
                align: "right" as const,
                render: (_: any, row: any) => (
                  <span className="exec-num" style={{ fontWeight: row.amount === null ? 400 : 600, color: row.amount === null ? "#8094ae" : "#364a63" }}>
                    {row.amount === null ? "—" : fmtQty(row.amount)}
                  </span>
                ),
              },
              {
                title: `₹ / ${unit}`,
                key: "perUnit",
                width: 120,
                align: "right" as const,
                render: (_: any, row: any) => {
                  const v = row.amount === null ? null : puDay(row.amount);
                  return (
                    <span className="exec-num" style={{ color: v === null ? "#8094ae" : "#526484" }}>
                      {v === null ? "—" : fmtPerUnit(v)}
                    </span>
                  );
                },
              },
            ]}
          />
        </div>

        <div className="pms-exec-heads-block">
          <div className="pms-exec-lines-head" style={{ marginBottom: 6 }}>
            <span>
              <span className="pms-eyebrow">Operational cost</span>
              <div className="pms-exec-hint">
                Qty × actual rate · target = hours entered
                {targetHours !== null ? ` (${fmtQty(String(Math.round(targetHours * 100) / 100))} h)` : ""} × the rate card's ₹/hr
              </div>
            </span>
            <span className="pms-exec-heads-figure">
              <span className="exec-num pms-exec-heads-subtotal">₹ {fmtQty(String(Math.round((totals.consumed + earlierConsumed) * 100) / 100))}</span>
              {noteDay(totals.consumed + earlierConsumed)}
            </span>
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            className="pms-exec-heads-table"
            dataSource={consumedRows}
            columns={[
              { title: "Head", dataIndex: "label", key: "label", width: 160 },
              {
                title: "Qty",
                key: "qty",
                width: 170,
                render: (_: any, row: any) => {
                  const rate = isPresent(row.normRate) ? Number(row.normRate) : null;
                  const target = rate !== null && targetHours !== null ? Math.round(rate * targetHours * 100) / 100 : null;
                  return (
                    <div>
                      <Tooltip title={row.earlier ? `${fmtQty(String(row.earlier.qty))} ${row.uomName} of this is from the ${row.earlier.from}` : ""}>
                        <InputNumber
                          stringMode
                          min={row.earlier ? String(row.earlier.qty) : "0"}
                          step="0.01"
                          placeholder="0.00"
                          value={inputValue(
                            row.earlier
                              ? String(Math.round((row.earlier.qty + (isPresent(row.draft.qty) ? Number(row.draft.qty) : 0)) * 10000) / 10000)
                              : row.draft.qty
                          )}
                          onChange={(v) => {
                            const total = fromInput(v);
                            if (!row.earlier) return setHead(row.key, { qty: total });
                            const share = (isPresent(total) ? Number(total) : 0) - row.earlier.qty;
                            setHead(row.key, { qty: share > 0.00005 ? String(Math.round(share * 10000) / 10000) : "" });
                          }}
                          disabled={disabled}
                          style={{ width: "100%" }}
                        />
                      </Tooltip>
                      {/* The norm for the hours entered — what this head should cost. */}
                      {rate === null ? (
                        <div className="pms-exec-hint">no rate card</div>
                      ) : target === null ? (
                        <div className="pms-exec-hint">norm ₹ {fmtQty(row.normRate)} / hr — enter hours</div>
                      ) : (
                        // The working (hours × norm ₹/hr) on hover, the figure inline.
                        <Tooltip
                          title={`${fmtQty(String(Math.round((targetHours as number) * 100) / 100))} h × ₹ ${fmtQty(row.normRate)}/hr`}
                        >
                          <div className="pms-exec-hint">target ₹ {fmtQty(String(target))}</div>
                        </Tooltip>
                      )}
                    </div>
                  );
                },
              },
              {
                title: "UOM",
                key: "uom",
                width: 130,
                render: (_: any, row: any) => (
                  <Tooltip title="Fixed for this head — from the UOM master">
                    <span className="pms-exec-fixed-uom">{row.uomName || "—"}</span>
                  </Tooltip>
                ),
              },
              {
                title: "Actual rate ₹ / unit",
                key: "rate",
                width: 170,
                render: (_: any, row: any) => (
                  <div>
                    <Tooltip
                      title={
                        row.earlier && row.earlier.rate !== null
                          ? isPresent(row.draft.rate)
                            ? `Today's rate. The earlier log was at ₹ ${fmtQty(String(Math.round(row.earlier.rate * 100) / 100))}`
                            : "Rate of the earlier log — type to set today's"
                          : ""
                      }
                    >
                      <InputNumber
                        stringMode
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={inputValue(
                          isPresent(row.draft.rate) || !row.earlier || row.earlier.rate === null
                            ? row.draft.rate
                            : String(Math.round(row.earlier.rate * 100) / 100)
                        )}
                        onChange={(v) => setHead(row.key, { rate: fromInput(v) })}
                        disabled={disabled}
                        style={{ width: "100%" }}
                      />
                    </Tooltip>
                  </div>
                ),
              },
              {
                title: "Actual ₹",
                key: "amount",
                width: 140,
                align: "right" as const,
                // The date's whole running cost for the head: earlier logs'
                // share plus today's qty x rate.
                render: (_: any, row: any) => {
                  const amount = row.earlier ? row.earlier.amount + (row.amount ?? 0) : row.amount;
                  return (
                    <span className="exec-num" style={{ fontWeight: amount === null ? 400 : 600, color: amount === null ? "#8094ae" : "#364a63" }}>
                      {amount === null ? "—" : fmtQty(String(Math.round(amount * 100) / 100))}
                    </span>
                  );
                },
              },
              {
                title: `₹ / ${unit}`,
                key: "perUnit",
                width: 120,
                align: "right" as const,
                render: (_: any, row: any) => {
                  const amount = row.earlier ? row.earlier.amount + (row.amount ?? 0) : row.amount;
                  const v = amount === null ? null : puDay(amount);
                  return (
                    <span className="exec-num" style={{ color: v === null ? "#8094ae" : "#526484" }}>
                      {v === null ? "—" : fmtPerUnit(v)}
                    </span>
                  );
                },
              },
            ]}
          />
        </div>

        <div className="pms-exec-heads-total">
          <span className="pms-exec-hint">
            {day ? `Machine total for ${dayjs(day.workDate).format("DD-MMM")} · fixed + operational` : "Machine total for the day · fixed + operational"}
          </span>
          <span className="pms-exec-heads-figure">
            <span className="exec-num" style={{ fontWeight: 600, fontSize: 15 }}>
              ₹ {fmtQty(String(Math.round((totals.total + earlierConsumed) * 100) / 100))}
            </span>
            {noteDay(totals.total + earlierConsumed)}
          </span>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------- hours by civil date
  // Each civil date with the machines it needs: Target hrs (norm per unit x the
  // day's output) and the Actual hrs box — the day's whole booking, earlier
  // logs plus this log's share. Only this log's share is saved, against that
  // civil date, and booked there exactly.
  const EPS = 0.00005;
  const num = (v: string) => (isPresent(v) ? Number(v) : 0);
  const q2 = (n: number) => String(Math.round(n * 10000) / 10000);
  const liveDay = (d: EquipmentLine["civilDays"][number]) => num(d.pendingQty) > 0 || num(d.bookedTodayQty) > 0;
  const earlierOf = (d: EquipmentLine["civilDays"][number]) => Math.max(0, num(d.bookedQty) - num(d.bookedTodayQty));
  const roomOf = (d: EquipmentLine["civilDays"][number]) => Math.max(0, num(d.requiredQty) - earlierOf(d));
  type DateRow =
    | { kind: "day"; key: string; workDate: string; open: boolean; producedQty: string; count: number; owed: number }
    | { kind: "machine"; key: string; line: EquipmentLine; day: EquipmentLine["civilDays"][number] }
    // Today's log that is NOT against a civil date (hours or cost heads typed
    // on the day itself, mobile, Excel) — kept visible and editable.
    | { kind: "otherHead"; key: string; count: number }
    | { kind: "other"; key: string; line: EquipmentLine };
  const dateRows: DateRow[] = [];
  {
    const byDate = new Map<string, { producedQty: string; items: Array<{ line: EquipmentLine; day: EquipmentLine["civilDays"][number] }> }>();
    // Every date still owed or booked today; the clicked date always (booked
    // or not), FIRST and open, the others collapsed beneath it (user, 2026-10-09).
    sheet.lines.forEach((line) =>
      line.civilDays
        .filter((d) => liveDay(d) || (focusDate !== null && focusDate !== undefined && d.workDate === focusDate && num(d.requiredQty) > 0))
        .forEach((d) => {
          const slot = byDate.get(d.workDate) ?? { producedQty: d.producedQty, items: [] };
          slot.items.push({ line, day: d });
          byDate.set(d.workDate, slot);
        })
    );
    Array.from(byDate.keys())
      .sort((a, b) => (a === focusDate ? -1 : b === focusDate ? 1 : a.localeCompare(b)))
      .forEach((workDate) => {
        const slot = byDate.get(workDate)!;
        const key = `D-${workDate}`;
        const open = dayOpen[key] ?? (!focusDate || workDate === focusDate);
        dateRows.push({
          kind: "day",
          key,
          workDate,
          open,
          producedQty: slot.producedQty,
          count: slot.items.length,
          owed: slot.items.filter(({ line, day }) => num(draftFor(line).pendingDays[day.workDate] ?? "") + EPS < roomOf(day)).length,
        });
        if (open) slot.items.forEach(({ line, day }) => dateRows.push({ kind: "machine", key: `${workDate}-${line.assetId}`, line, day }));
      });
    const listed = new Set<number>();
    byDate.forEach((slot) => slot.items.forEach(({ line }) => listed.add(line.assetId)));
    const others = sheet.lines.filter((line) => {
      if (listed.has(line.assetId)) return false;
      const d = draftFor(line);
      return (
        isPresent(d.hoursWorked) ||
        VARIABLE_HEAD_KEYS.some((key) => isPresent(d.heads[key].qty) || isPresent(d.heads[key].rate))
      );
    });
    if (others.length) {
      dateRows.push({ kind: "otherHead", key: "OTHER", count: others.length });
      others.forEach((line) => dateRows.push({ kind: "other", key: `O-${line.assetId}`, line }));
    }
  }
  const setDayHours = (line: EquipmentLine, workDate: string, hours: string) =>
    onChange(line.assetId, { pendingDays: { ...draftFor(line).pendingDays, [workDate]: hours } });
  const dateSpan = (row: DateRow, key: string) =>
    row.kind !== "day" && row.kind !== "otherHead"
      ? {}
      : key === "machine"
        ? { className: "pms-mat-group-cell" }
        : key === "target"
          ? { colSpan: 3, className: "pms-mat-group-cell" }
          : { colSpan: 0 };
  const dateColumns = [
    {
      title: "Civil date / Machine",
      key: "machine",
      width: w(340),
      onCell: (row: DateRow) => dateSpan(row, "machine"),
      render: (_: any, row: DateRow) =>
        row.kind === "otherHead" ? (
          <span className="pms-mat-group-name">
            <span style={{ color: "#022c57", fontWeight: 600 }}>Not against a civil date</span>
            <span className="pms-exec-group-count">today's log</span>
          </span>
        ) : row.kind === "day" ? (
          <span className="pms-mat-group-name" onClick={() => setDayOpen((current) => ({ ...current, [row.key]: !row.open }))}>
            {row.open ? <CaretDownOutlined /> : <CaretRightOutlined />}
            <span style={{ color: "#022c57", fontWeight: 600 }}>{dayjs(row.workDate).format("DD-MMM-YYYY")}</span>
            {row.workDate === focusDate && <span className="pms-exec-count-badge">opened</span>}
            {row.producedQty !== "" && (
              <span className="pms-exec-group-count">
                produced {fmtQty(row.producedQty)} {sheet.workUomName}
              </span>
            )}
          </span>
        ) : (
          <div style={{ paddingLeft: 18 }}>
            <div style={{ color: "#022c57", fontWeight: 600 }}>{row.line.assetName}</div>
            <div className="pms-exec-hint">{row.line.assetCode}</div>
          </div>
        ),
    },
    {
      title: "Target hrs",
      key: "target",
      width: w(140),
      align: "right" as const,
      onCell: (row: DateRow) => dateSpan(row, "target"),
      render: (_: any, row: DateRow) => {
        if (row.kind === "otherHead") {
          return (
            <span className="pms-exec-hint" style={{ display: "block", textAlign: "right" }}>
              {row.count} machine{row.count === 1 ? "" : "s"} · booked on its own day, else oldest civil date first
            </span>
          );
        }
        if (row.kind === "other") return <span style={{ color: "#8094ae" }}>—</span>;
        if (row.kind === "day") {
          const owing = dateRows.filter(
            (r): r is Extract<DateRow, { kind: "machine" }> =>
              r.kind === "machine" && r.day.workDate === row.workDate && num(draftFor(r.line).pendingDays[row.workDate] ?? "") + EPS < roomOf(r.day)
          );
          return (
            <span className="pms-mat-day-actions" style={{ justifyContent: "flex-end", width: "100%" }}>
              <span className="pms-exec-hint">
                {row.count} machine{row.count === 1 ? "" : "s"} · {row.owed} owed
              </span>
              {!disabled && owing.length > 0 && (
                <Button size="small" onClick={() => owing.forEach((r) => setDayHours(r.line, row.workDate, q2(roomOf(r.day))))}>
                  Fill this date
                </Button>
              )}
            </span>
          );
        }
        return (
          <span className="exec-num" style={{ color: "#526484" }}>
            {fmtQty(row.day.requiredQty)} h
          </span>
        );
      },
    },
    {
      title: "Actual hrs",
      key: "actual",
      width: w(200),
      onCell: (row: DateRow) => dateSpan(row, "actual"),
      render: (_: any, row: DateRow) => {
        if (row.kind === "other") {
          const draft = draftFor(row.line);
          return (
            <InputNumber
              stringMode
              min="0"
              step="0.5"
              placeholder="0.0"
              value={inputValue(draft.hoursWorked)}
              onChange={(v) => onChange(row.line.assetId, { hoursWorked: fromInput(v) })}
              disabled={disabled}
            />
          );
        }
        if (row.kind !== "machine") return null;
        const { line, day } = row;
        const today = draftFor(line).pendingDays[day.workDate] ?? "";
        const earlier = earlierOf(day);
        const required = num(day.requiredQty);
        const shown = isPresent(today) ? q2(earlier + num(today)) : earlier > EPS ? q2(earlier) : "";
        const room = roomOf(day);
        const fixed = room <= EPS && num(day.bookedTodayQty) <= EPS;
        return (
          <div>
            <Tooltip
              title={
                fixed
                  ? "Fully booked by an earlier day's log"
                  : earlier > EPS
                    ? `${fmtQty(q2(earlier))} h of this was booked by an earlier day's log`
                    : ""
              }
            >
              <InputNumber
                stringMode
                min={q2(earlier)}
                step="0.5"
                placeholder="0.0"
                value={inputValue(shown)}
                onChange={(v) => {
                  const total = fromInput(v);
                  if (total === "") return setDayHours(line, day.workDate, "");
                  const share = num(total) - earlier;
                  setDayHours(line, day.workDate, share > EPS ? q2(share) : "");
                }}
                disabled={disabled || fixed}
              />
            </Tooltip>
            {!fixed && room > EPS && num(today) + EPS < room && !disabled && (
              <div className="pms-exec-hint">
                <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => setDayHours(line, day.workDate, q2(room))}>
                  use {fmtQty(q2(required))} h
                </Button>
              </div>
            )}
            {num(shown) > required + EPS && (
              <div className="pms-exec-hint pms-exec-hint--amber">over target by {fmtQty(q2(num(shown) - required))} h</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Cost heads (today's log)",
      key: "heads",
      width: w(220),
      onCell: (row: DateRow) => dateSpan(row, "heads"),
      render: (_: any, row: DateRow) =>
        row.kind === "machine" ? headsToggle(row.line, row.key, row.day) : row.kind === "other" ? headsToggle(row.line, row.key) : null,
    },
  ];

  const problems = sheet.lines.flatMap((line) =>
    lineErrors(entry.draft[line.assetId]).map((text) => `${line.assetName}: ${text}`)
  );

  return (
    <div className="pms-exec-lines">
      <div className="pms-exec-lines-head">
        <span className="pms-exec-hint">
          {sheet.lines.length} machine{sheet.lines.length === 1 ? "" : "s"} ·{" "}
          {sheet.producedTillDate === "" ? (
            <span className="pms-exec-hint--amber">no civil work on this task yet</span>
          ) : (
            <>
              civil done till date <span className="exec-num">{fmtQty(sheet.producedTillDate)}</span> {sheet.workUomName}
            </>
          )}
          {sheet.hasEntryForDate ? " · log recorded" : " · no log recorded yet"}
          {taskTotal > 0 && (
            <>
              {" · machinery today "}
              <span className="exec-num" style={{ color: "#364a63", fontWeight: 600 }}>₹ {fmtQty(taskTotal)}</span>
              {taskPerUnit !== null && (
                <>
                  {" · "}
                  <span className="exec-num" style={{ color: "#364a63", fontWeight: 600 }}>
                    ₹ {fmtPerUnit(taskPerUnit)} / {unit}
                  </span>
                </>
              )}
            </>
          )}
        </span>
      </div>
      {focusDate && (
        <div className="pms-exec-hint" style={{ marginBottom: 6 }}>
          Opened from <b style={{ color: "#364a63" }}>{dayjs(focusDate).format("DD-MMM-YYYY")}</b> — the other civil dates still
          owed are below it; click a date to open it.
        </div>
      )}
      <Table
        size="small"
        rowKey="key"
        className="pms-exec-heads-table pms-mat-table"
        dataSource={dateRows}
        columns={dateColumns as any}
        pagination={false}
        rowClassName={(row: DateRow) => (row.kind === "day" || row.kind === "otherHead" ? "pms-mat-group-row" : "")}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys: openHeads,
          rowExpandable: (row: DateRow) => row.kind === "machine" || row.kind === "other",
          expandedRowRender: (row: DateRow) =>
            row.kind === "machine" ? renderHeads(row.line, row.day) : row.kind === "other" ? renderHeads(row.line) : null,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={focusDate ? `No machine hours owed for ${dayjs(focusDate).format("DD-MMM-YYYY")}` : "No machine hours owed to earlier civil work"}
            />
          ),
        }}
      />
      {problems.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 10 }}
          message={problems.length === 1 ? problems[0] : `${problems.length} rows need attention`}
          description={
            problems.length > 1 ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {problems.map((text) => (
                  <li key={text}>{text}</li>
                ))}
              </ul>
            ) : undefined
          }
        />
      )}
      {!inDrawer && (
      <div className="pms-exec-lines-foot">
        <span className="pms-exec-hint">
          Blank = not recorded. A machine with nothing recorded is left out of the log.
        </span>
        {!locked && (
          <span style={{ display: "inline-flex", gap: 8 }}>
            <Button size="small" onClick={onReset} disabled={!dirty || entry.saving}>
              Reset
            </Button>
            <Tooltip title={invalid ? "Fix the flagged rows to save" : ""}>
              <Button
                size="small"
                type="primary"
                icon={<SaveOutlined />}
                onClick={onSave}
                disabled={!dirty || invalid}
                loading={entry.saving}
              >
                Save {task.taskSequenceId}
              </Button>
            </Tooltip>
          </span>
        )}
      </div>
      )}
    </div>
  );
};

export default MechanicalSection;
